/* -----------------Globals--------------- */
import { useState } from "react";

import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";
/* -----------------UI--------------- */
import Box from "@memorang/ui/components/Box";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import Loader from "@memorang/ui/components/Loader";
import StudyPackApplet from "@memorang/ui/components/StudyPackApplet";
import useBreakpoints from "@memorang/ui/useBreakpoints";
import { useNavigation, useRouter } from "expo-router";
import { getDashboardData } from "../helpers/dashboard-data";

/* -----------------Types--------------- */
import type { MixedItemDist } from "@memorang/types";

/* -----------------Child components--------------- */

import { events } from "@constants/tracking";
/* -----------------Analytics--------------- */
import { trackCustomEvent } from "analytics";

import { appBarMeta } from "@constants/appbar";
import { isWeb } from "@helpers/platform";
import { useDevice } from "@hooks/useDevice";

import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import useMixedItemDist from "@hooks/useMixedItemDist";
import Container from "@memorang/ui/components/Container";
import type { QuickActionsChipType } from "@memorang/ui/types";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
import { PurchaseDialog } from "../components/PurchaseDialog";
import { NestedExamChipsContainer } from "../containers/NestedExamChipsContainer";
import { getQuickActionsChips, getWeakTagsIds } from "../helpers/common";

const StudyPackContainer = () => {
	const { data: mixedItemDistResponse } = useMixedItemDist(
		true,
		"GET_STUDYPACK_DISTRIBUTION",
	);

	const {
		currentExamContext: { examName },
	} = useExamContext();

	const { smDown } = useBreakpoints();
	const finalHeaderTitle = smDown ? examName : appBarMeta["product-home"].title;
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();

	const CustomLoader = () => {
		return (
			<Box flex={1}>
				<CustomAppBar
					title={finalHeaderTitle}
					navigation={navigation}
					handleTrackEvent={() => {
						trackCustomEvent({
							eventName: events.commandBarClicked,
							option: "command_bar",
						});
					}}
				/>
				<Container maxWidth="sm">
					<NestedExamChipsContainer
						disabled
						containerStyle={{ paddingTop: 24, paddingBottom: 16 }}
					/>
					<Box
						flex={1}
						justifyContent="center"
						alignItems="center"
						height="100%"
					>
						<Loader />
					</Box>
				</Container>
			</Box>
		);
	};

	if (!mixedItemDistResponse) {
		return <CustomLoader />;
	}

	return (
		<>
			{mixedItemDistResponse && (
				<StudyPackRenderer
					mixedItemDistResponse={mixedItemDistResponse}
					headerTitle={finalHeaderTitle}
				/>
			)}
		</>
	);
};

export default StudyPackContainer;

const StudyPackRenderer = ({
	mixedItemDistResponse,
	headerTitle,
}: {
	mixedItemDistResponse: MixedItemDist;
	headerTitle: string;
}) => {
	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);
	const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const { handleFeedbackNavigation } = useCustomNavigation();
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();

	const toggleShowFeedbackDialog = () => {
		setShowFeedbackDialog((prev) => !prev);
	};
	const toggleShowSessionConfigDialog = () => {
		setShowSessionConfigDialog((prev) => !prev);
	};
	const { isMobile, isMediumWindowSize } = useDevice();
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

	const toggleShowPurchaseDialog = () => {
		setShowPurchaseDialog((prev) => !prev);
	};

	const groupByCategory = useAppStore((store) => store.groupByCategory);

	const router = useRouter();
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();
	const data = mixedItemDistResponse;

	const handleNavigateToIAP = () => {
		trackCustomEvent({
			eventName: events.upgradeChipClicked,
		});
		if (isMobile || isMediumWindowSize) {
			router.push({
				pathname: "/purchase",
				params: {
					numItems,
					productType: "STUDY_PACK",
				},
			});
		} else {
			toggleShowPurchaseDialog();
		}
	};
	const { distribution, productImage, productTitle, sectionTags, numItems } =
		getDashboardData(data, handleNavigateToIAP, groupByCategory);

	const weakTagsIds = getWeakTagsIds(data);

	const handleQuickActionPress = (action: QuickActionsChipType["type"]) => {
		trackCustomEvent({
			eventName: events.quickActionClicked,
			action,
		});
		switch (action) {
			case "weak":
				router.push({
					pathname: "/(protected)/session/customize",
					params: {
						selectedTagIds: weakTagsIds,
					},
				});
				break;
			case "leaderboard":
				router.push("/(protected)/(tabs)/leaderboard");
				break;
			case "progress":
				router.push("/(protected)/(tabs)/progress");
				break;
			default:
				break;
		}
	};

	//TODO:Below hooks look ugly to get just the leaderboard work, instead the api should return the stats

	const handleClickListItem = (selectedTagId: number, locked?: boolean) => {
		if (locked) {
			handleNavigateToIAP();
		} else if (isWeb) {
			if (isMobile) {
				router.push({
					pathname: "/session/customize",
					params: {
						selectedTagIds: [selectedTagId],
					},
				});
			} else {
				setSelectedTagId(selectedTagId);
				toggleShowSessionConfigDialog();
			}
		} else {
			router.push({
				pathname: "/session/customize",
				params: {
					selectedTagIds: [selectedTagId],
				},
			});
		}
	};

	const actionChips = getQuickActionsChips(studyPackCollectionId);

	return (
		<>
			<StudyPackApplet
				renderNestedExamChips={() => (
					<NestedExamChipsContainer containerStyle={{ paddingTop: 24 }} />
				)}
				distribution={distribution}
				productTitle={productTitle}
				productImage={productImage}
				sectionTags={sectionTags}
				handleNavigateToIAP={handleNavigateToIAP}
				handleQuickActionPress={handleQuickActionPress}
				bottomFab={{
					icon: "play-outline",
					label: "Quick Study",
					onPress: () => router.push("/(protected)/session/customize"),
				}}
				actionChips={actionChips}
				handleClickListItem={handleClickListItem}
				handleFeedbackNavigation={() =>
					handleFeedbackNavigation(toggleShowFeedbackDialog)
				}
				customAppBar={{
					title: headerTitle,
					navigation,
					handleTrackEvent: () => {
						trackCustomEvent({
							eventName: events.commandBarClicked,
							option: "command_bar",
						});
					},
				}}
			/>

			{showPurchaseDialog ? (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog}
					productType={"STUDY_PACK"}
				/>
			) : null}
			{isWeb && showFeedbackDialog && (
				<FeedbackDialogContainer
					visible={showFeedbackDialog}
					onDismiss={toggleShowFeedbackDialog}
				/>
			)}
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleShowSessionConfigDialog}
					selectedTagIds={[selectedTagId!]}
				/>
			) : null}
		</>
	);
};
